import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FILE_TOO_LARGE } from 'utils/constants/errors'
import { maxImageUploadBytes, maxVideoUploadBytes } from 'utils/constants/file'
import { getFileTypeFromMimeType } from 'utils/media'

import { FileType } from 'types/file'
import { Media } from 'types/media'

interface Return {
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleReset: () => void
  selectedFile?: SelectedFile
  selectedFileRef?: React.MutableRefObject<SelectedFile | undefined>
  selectedFileType?: FileType
  selectedFileUrl?: string
}

type SelectedFile = File | Media

export const useFileSelect = (initialFile?: SelectedFile): Return => {
  const [selectedFile, setSelectedFile] = useState<SelectedFile | undefined>(
    initialFile ?? undefined,
  )

  const selectedFileRef = useRef<SelectedFile | undefined>(selectedFile)

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files?.length) {
      const file = event.target.files[0]
      const maxUploadSize =
        getFileTypeFromMimeType(file.type) === 'image' ? maxImageUploadBytes : maxVideoUploadBytes

      if (file.size > maxUploadSize) {
        throw new Error(FILE_TOO_LARGE.message)
      }

      setSelectedFile(file)
    }
  }, [])

  const handleReset = useCallback(() => {
    setSelectedFile(undefined)
  }, [])

  const selectedFileUrl = useMemo(() => {
    if (!selectedFile) {
      return undefined
    }
    return 'url' in selectedFile ? selectedFile.url : URL.createObjectURL(selectedFile)
  }, [selectedFile])

  const selectedFileType = useMemo(() => {
    if (!selectedFile) {
      return undefined
    }
    return 'mimeType' in selectedFile
      ? getFileTypeFromMimeType(selectedFile.mimeType)
      : getFileTypeFromMimeType(selectedFile.type)
  }, [selectedFile])

  useEffect(() => {
    selectedFileRef.current = selectedFile
  }, [selectedFile])

  useEffect(() => {
    setSelectedFile(initialFile)
  }, [initialFile])

  return {
    handleReset,
    handleFileSelect,
    selectedFile,
    selectedFileRef,
    selectedFileType,
    selectedFileUrl,
  }
}
